import { Shield, Users, Tag, ArrowLeft, BarChart, Megaphone, Search, Cloud, MessageSquare, TextSearch, Webhook, BadgeDollarSign, BellRing, Store, Mail, ImageDown, Activity, Gift, Eye, Sprout, HeartPulse, SlidersHorizontal, ServerCog, ChartLine } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { Navigate } from "react-router-dom";
import { useState } from "react";
import UserDetail from "../components/UserDetail";
import { type User, getAdminUser } from "../services/UserService";
import { UsersTab, CategoriesTab, AnalyticsTab, AnnouncementsTab, RetailerSettingsTab, ProductSearchTab, PriceCaptureTab, ProxyUsageTab, LeadCategoriesTab, KeywordSearchesTab, DiscordWebhooksTab, ProductAffiliatesTab, RetailerWebhooksTab, StoreRunnerTab, EmailMonitoringTab, StoreProductionTab, FreeAfterCashbackTab, SearchHarvestTab, FeedHealthTab, RetailerTrendsTab, SystemsTab } from "../components/adminTabs";

type AdminTab = "systems" | "feed-health" | "retailer-health" | "users" | "categories" | "analytics" | "store-production" | "email-monitoring" | "announcements" | "retailer-settings" | "store-runner" | "product-search" | "price-capture" | "proxy" | "lead-categories" | "keyword-searches" | "discord-webhooks" | "product-affiliates" | "retailer-webhooks" | "free-after-cashback" | "search-harvest";

const NAV_SECTIONS: { title: string; items: { id: AdminTab; label: string; icon: typeof Users }[] }[] = [
  {
    title: "Manage",
    items: [
      { id: "users", label: "Users", icon: Users },
      { id: "retailer-settings", label: "Retailer settings", icon: SlidersHorizontal },
      { id: "categories", label: "Categories", icon: Tag },
      { id: "product-affiliates", label: "Product affiliates", icon: BadgeDollarSign },
      { id: "lead-categories", label: "Lead categories", icon: MessageSquare },
      { id: "keyword-searches", label: "Keyword searches", icon: TextSearch },
      { id: "discord-webhooks", label: "Discord webhooks", icon: Webhook },
      { id: "retailer-webhooks", label: "Retailer webhooks", icon: BellRing },
      { id: "announcements", label: "Announcements", icon: Megaphone },
    ],
  },
  {
    title: "Monitor",
    items: [
      { id: "systems", label: "Systems", icon: ServerCog },
      { id: "feed-health", label: "Feed health", icon: HeartPulse },
      { id: "retailer-health", label: "Retailer health", icon: ChartLine },
      { id: "analytics", label: "Analytics", icon: BarChart },
      { id: "store-production", label: "Store production", icon: Activity },
      { id: "email-monitoring", label: "Email", icon: Mail },
      { id: "store-runner", label: "Store runner", icon: Store },
      { id: "product-search", label: "Product search", icon: Search },
      { id: "price-capture", label: "Price capture", icon: ImageDown },
      { id: "proxy", label: "Proxy", icon: Cloud },
      { id: "free-after-cashback", label: "Free after cash back", icon: Gift },
      { id: "search-harvest", label: "Search harvest", icon: Sprout },
    ],
  },
];

const ALL_NAV_ITEMS = NAV_SECTIONS.flatMap((s) => s.items);

export default function AdminPage() {
  const { user, activateFreePlanPreview } = useUserContext();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>("users");

  if (!user?.admin) return <Navigate to="/" replace />;

  const handleUserSelect = async (userId: string) => {
    setLoadingUser(true);
    try {
      const userClicked = await getAdminUser(userId);
      setSelectedUser(userClicked);
    }
    finally { setLoadingUser(false); }
  };
  const handleBackToUsers = () => setSelectedUser(null);
  const handleUserUpdated = (updatedUser: User) => setSelectedUser(updatedUser);
  const handleTabSelect = (tabId: AdminTab) => { setActiveTab(tabId); setSelectedUser(null); };

  const activeItem = ALL_NAV_ITEMS.find((i) => i.id === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-5 lg:py-7 lg:grid lg:grid-cols-[212px_minmax(0,1fr)] lg:gap-8 lg:items-start">

        {/* ── Rail (desktop) ─────────────────────────────── */}
        <aside className="hidden lg:block sticky top-6">
          <div className="flex items-center gap-2.5 px-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center shadow-sm shadow-red-600/30">
              <Shield className="w-4.5 h-4.5 text-white" strokeWidth={2.25} />
            </div>
            <div className="leading-tight">
              <p className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">DealHawk</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-red-600 dark:text-red-400">Admin</p>
            </div>
          </div>

          <button
            type="button"
            onClick={activateFreePlanPreview}
            className="mb-5 flex w-full items-center gap-2 rounded-lg bg-white px-2.5 py-2 text-left text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-100 hover:text-gray-950 dark:bg-dark-800 dark:text-dark-200 dark:ring-dark-700 dark:hover:bg-dark-700 dark:hover:text-white"
            title="Use the app with Free-plan limits without changing your subscription"
          >
            <Eye className="h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />
            Preview Free plan
          </button>

          <nav className="space-y-5">
            {NAV_SECTIONS.map((section) => (
              <div key={section.title}>
                <p className="px-2 mb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 dark:text-dark-400">{section.title}</p>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const active = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleTabSelect(item.id)}
                        className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-[13px] transition-colors ${active
                          ? "bg-white dark:bg-dark-800 text-gray-900 dark:text-white font-semibold shadow-sm ring-1 ring-gray-200 dark:ring-dark-700"
                          : "text-gray-500 dark:text-dark-300 font-medium hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-800/60"
                          }`}
                      >
                        <item.icon className={`w-4 h-4 shrink-0 ${active ? "text-red-600 dark:text-red-400" : "text-gray-400 dark:text-dark-400"}`} strokeWidth={active ? 2.25 : 2} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* ── Content ────────────────────────────────────── */}
        <div className="min-w-0">
          {/* Mobile nav: brand + scrollable pills */}
          <div className="lg:hidden mb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-red-600 flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" strokeWidth={2.25} />
              </div>
              <p className="text-sm font-bold text-gray-900 dark:text-white">DealHawk <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-red-600 dark:text-red-400 ml-1">Admin</span></p>
              <button
                type="button"
                onClick={activateFreePlanPreview}
                className="ml-auto flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:text-dark-200 dark:ring-dark-700"
              >
                <Eye className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                Preview Free
              </button>
            </div>
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1">
              {ALL_NAV_ITEMS.map((item) => {
                const active = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleTabSelect(item.id)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${active
                      ? "bg-gray-900 dark:bg-white text-white dark:text-dark-900"
                      : "bg-white dark:bg-dark-800 text-gray-600 dark:text-dark-300 ring-1 ring-gray-200 dark:ring-dark-700"
                      }`}
                  >
                    <item.icon className="w-3.5 h-3.5" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Breadcrumb header when viewing a user */}
          {(selectedUser || loadingUser) && (
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={handleBackToUsers}
                className="flex items-center gap-1.5 px-2.5 py-1.5 -ml-1 rounded-lg text-[13px] font-medium text-gray-500 dark:text-dark-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Users
              </button>
              <span className="text-gray-300 dark:text-dark-600">/</span>
              <span className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                {loadingUser ? "Loading…" : selectedUser?.name || selectedUser?.userId}
              </span>
            </div>
          )}

          {loadingUser ? (
            <div className="flex items-center justify-center h-72">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 dark:border-red-400" />
            </div>
          ) : selectedUser ? (
            <UserDetail user={selectedUser} onBack={handleBackToUsers} onUserUpdated={handleUserUpdated} />
          ) : (
            <>
              <h1 className="sr-only">{activeItem?.label}</h1>
              {activeTab === "users" && <UsersTab onUserSelect={handleUserSelect} />}
              {activeTab === "categories" && <CategoriesTab />}
              {activeTab === "systems" && <SystemsTab />}
              {activeTab === "feed-health" && <FeedHealthTab onUserSelect={handleUserSelect} />}
              {activeTab === "retailer-health" && <RetailerTrendsTab />}
              {activeTab === "analytics" && <AnalyticsTab />}
              {activeTab === "store-production" && <StoreProductionTab />}
              {activeTab === "email-monitoring" && <EmailMonitoringTab />}
              {activeTab === "announcements" && <AnnouncementsTab />}
              {activeTab === "retailer-settings" && <RetailerSettingsTab />}
              {activeTab === "store-runner" && <StoreRunnerTab />}
              {activeTab === "product-search" && <ProductSearchTab />}
              {activeTab === "price-capture" && <PriceCaptureTab />}
              {activeTab === "proxy" && <ProxyUsageTab />}
              {activeTab === "lead-categories" && <LeadCategoriesTab />}
              {activeTab === "keyword-searches" && <KeywordSearchesTab />}
              {activeTab === "discord-webhooks" && <DiscordWebhooksTab />}
              {activeTab === "product-affiliates" && <ProductAffiliatesTab />}
              {activeTab === "retailer-webhooks" && <RetailerWebhooksTab />}
              {activeTab === "free-after-cashback" && <FreeAfterCashbackTab />}
              {activeTab === "search-harvest" && <SearchHarvestTab />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
